import { useUser } from 'contexts/UserContext';
import { IssueMode, IssueType } from 'pages/Desk/issues/shared';
import { clearDeskStateBrowserStorage } from 'pages/Desk/issues/state/shared';
import { DeskState, DeskStateValues } from 'pages/Desk/issues/state/types';
import * as React from 'react';
import { ReactChild, useCallback, useMemo } from 'react';
import {
    createZustandContext,
    getKeyedZustandStoreChangeModuleHook,
    getKeyedZustandStoreShallowHook,
    KeyedStore,
    useZustandStore,
    wrapWithZustandContext,
    ZustandContextInitializer,
    ZustandContextProvider,
    ZustandStore,
} from 'shared/zustand_context/zustand_context';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { getDeskStateInitializer } from './initializer';

/** Don't use this directly outside of src/pages/Desk/issues/state/* */
export const DeskContext = createZustandContext<KeyedDeskStore>();

export function buildDeskModuleKey<
    TLocationId extends string,
    TIssueType extends IssueType,
    TIssueMode extends IssueMode,
>(
    locationId: TLocationId,
    stateFilter: TIssueType,
    issueType: TIssueMode,
): DeskModuleKey<TLocationId, TIssueType, TIssueMode> {
    return `${locationId};${stateFilter};${issueType}`;
}

export type DeskModuleKey<
    TLocationId extends string = string,
    TIssueType extends IssueType = IssueType,
    TIssueMode extends IssueMode = IssueMode,
> = `${TLocationId};${TIssueType};${TIssueMode}`;

/**
 * Used to lookup multiple values from the desk state.
 * @example
 * const { sort, filters } = useDeskValues(({ sort, filters }) => ({
 *     sort, filters
 * }));
 */
export const useDeskValues = getKeyedZustandStoreShallowHook<DeskState, DeskModuleKey>(
    DeskContext,
);

export const useChangeDeskModule = getKeyedZustandStoreChangeModuleHook<
    DeskState,
    DeskModuleKey
>(DeskContext);

export const useResetDeskStorage = (tab: string, mode: IssueMode) => {
    const email = useUser().user?.Email;

    return useCallback(() => {
        if (email) {
            clearDeskStateBrowserStorage(email, tab, mode);
        }
    }, [email, mode, tab]);
};

export type DeskProviderProps = Readonly<{
    children?: ReactChild;
    overrides?: Partial<DeskStateValues>;
}>;

export function DeskProvider({ children, overrides }: DeskProviderProps) {
    const initializer = useMemo(() => getDeskFullInitializer(overrides), [overrides]);

    return (
        <ZustandContextProvider ctx={DeskContext} initializer={initializer}>
            {children}
        </ZustandContextProvider>
    );
}

export type ChildDeskProviderProps = Readonly<{ children?: ReactChild }>;

export function ChildDeskProvider({ children }: ChildDeskProviderProps) {
    const store = useZustandStore(DeskContext);
    const overrides = useMemo(() => store.getState().current().getState(), [store]);

    return <DeskProvider overrides={overrides}>{children}</DeskProvider>;
}

type KeyedDeskStore = KeyedStore<DeskModuleKey, DeskState>;

export function wrapWithDeskContext<TProps extends object>(
    Component: React.ComponentType<TProps>,
    overrides: Partial<DeskStateValues> = {},
) {
    return wrapWithZustandContext(
        Component,
        DeskContext,
        getDeskFullInitializer(overrides),
    );
}

/**
 * The key to the store containing active desk module's storage key.
 * This value is given its own store to allow resolving the correct {@link KeyedDeskStore}
 * value when it is not inferrable from the URL. (e.g. preserving the Issue Details page's
 * Issue Queue between browser page refreshes.)
 * @see DeskModuleKey
 */
const ActiveDeskModuleKey = 'active-desk-module-key';

export const getSessionModuleInfo = () => {
    const moduleKey = sessionStorage.getItem(ActiveDeskModuleKey);

    if (!moduleKey) {
        return undefined;
    }

    const [locationId, issueType, issueMode] = moduleKey.split(';');

    return {
        locationId,
        issueType: issueType as IssueType,
        issueMode: issueMode as IssueMode,
    };
};

function getDeskFullInitializer(
    overrides: Partial<DeskStateValues> = {},
): ZustandContextInitializer<KeyedDeskStore> {
    return (set, get) => ({
        moduleKey:
            (sessionStorage.getItem(ActiveDeskModuleKey) as DeskModuleKey | null) ??
            undefined,
        setModuleKey: (moduleKey: DeskModuleKey | undefined) => {
            set({ moduleKey });
            sessionStorage.setItem(ActiveDeskModuleKey, moduleKey ?? 'nokey');
        },
        stores: new Map<DeskModuleKey, ZustandStore<DeskState>>(),
        current: () => {
            const current = get();
            return current.get(current.moduleKey);
        },
        get: (moduleKey) => {
            const realKey = moduleKey ?? 'nokey';
            const currentStores = get().stores;
            let store = currentStores.get(realKey);
            if (store === undefined) {
                store = create(
                    persist(getDeskStateInitializer(overrides), {
                        name: realKey,
                        storage: createJSONStorage(() => sessionStorage),
                    }),
                );
                set({ stores: new Map(currentStores).set(realKey, store) });
            }
            return store;
        },
    });
}
